import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Slider,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload,
  RecordVoiceOver,
  PlayCircleOutline,
  PauseCircleOutline,
  QuestionAnswer,
  Send,
  ErrorOutline,
  CheckCircleOutline,
} from '@mui/icons-material';
import {
  Container,
  LeftPane,
  RightPane,
  TranscriptBox,
  SummaryBox,
} from './TranslationTranscriptionContainer.styles';
import { transcriptionApi } from '../../api/api';

function formatTime(seconds) {
  if (isNaN(seconds) || seconds < 0) {
    return '00:00';
  }
  const date = new Date(0);
  date.setSeconds(seconds);
  const timeString = date.toISOString().substring(11, 19);
  return seconds >= 3600 ? timeString : timeString.substring(3);
}

export default function TranslationTranscriptionContainer() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pollingStatus, setPollingStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [transcriptId, setTranscriptId] = useState(null);
  const [response, setResponse] = useState(null);
  const [question, setQuestion] = useState('');
  const [audioSrc, setAudioSrc] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [chatQueries, setChatQueries] = useState([]);
  const [chatResponses, setChatResponses] = useState([]);

  const audioRef = useRef(null);
  const highlightedWordRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioSrc) {
        URL.revokeObjectURL(audioSrc);
      }
    };
  }, [audioSrc]);

  const handleSubmitQuery = async (query) => {
    const transcription = response?.text;
    if (!transcription) {
      throw Error('There is no transcription provided.');
    }
  
    setChatQueries((prev) => [...prev, query]);
  
    try {
      const result = await transcriptionApi.queryTranscription(query, transcription);
      
      const rawContent = result?.message?.content;
      let parsedText = 'Invalid response format.';
      
      if (Array.isArray(rawContent)) {
        parsedText = rawContent.map((item) => item.text).join('\n\n');
      } else if (typeof rawContent === 'string') {
        parsedText = rawContent;
      }
  
      setChatResponses((prev) => [...prev, parsedText]);
    } catch (error) {
      console.error('Query failed:', error);
      setChatResponses((prev) => [...prev, 'Error fetching response.']);
    }
  };

  const resetState = () => {
    setFile(null);
    setAudioSrc(null);
    setResponse(null);
    setTranscriptId(null);
    setIsProcessing(false);
    setPollingStatus('idle');
    setErrorMessage('');
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }

  const handleFileUpload = (e) => {
    resetState();
    const uploaded = e.target.files[0];
    if (uploaded && uploaded.type.startsWith('audio/')) {
      setFile(uploaded);
      const url = URL.createObjectURL(uploaded);
      setAudioSrc(url);
    } else {
      console.warn('Please upload a valid audio file.');
      setErrorMessage('Please upload a valid audio file.');
      setPollingStatus('error');
      setFile(null);
      setAudioSrc(null);
    }
  };

  const handleProcess = async () => {
    if (!file) return;

    setFile(file);
    setAudioSrc(audioSrc);
    setIsProcessing(true);
    setPollingStatus('uploading');
    setErrorMessage('');

    try {
      const uploadResponse = await transcriptionApi.uploadAudioFile(file);
      const audioUrl = uploadResponse?.upload_url;

      if (!audioUrl) {
        throw new Error("Upload failed: No audio URL returned.");
      }

      setPollingStatus('pending');

      const submitResponse = await transcriptionApi.submitTranscriptionJob(audioUrl);
      const newTranscriptId = submitResponse?.id;

      if (!newTranscriptId) {
        throw new Error("Submission failed: No transcript ID returned.");
      }

      setTranscriptId(newTranscriptId);

    } catch (error) {
      console.error("Processing failed:", error);
      setErrorMessage(error.message || "Failed to start transcription process.");
      setIsProcessing(false);
      setPollingStatus('error');
      setTranscriptId(null);
    }
  };

  useEffect(() => {
    if (pollingIntervalRef.current && pollingStatus !== 'pending') {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (pollingStatus === 'pending' && transcriptId) {
      pollingIntervalRef.current = setInterval(async () => {
        console.log(`Polling status for ${transcriptId}...`);
        try {
          const result = await transcriptionApi.getTranscriptionResult(transcriptId);

          if (result.status === 'completed') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setResponse(result);
            setIsProcessing(false);
            setPollingStatus('completed');
            setErrorMessage('');
            setTranscriptId(null);
          } else if (result.status === 'error') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setErrorMessage(result.error || 'Transcription failed during processing.');
            setIsProcessing(false);
            setPollingStatus('error');
            setTranscriptId(null);
          } else if (result.status === 'queued' || result.status === 'processing') {
            setPollingStatus('pending');
          } else {
            console.warn("Unexpected transcript status:", result.status);
          }
        } catch (pollError) {
          console.error("Polling error:", pollError);
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setErrorMessage(pollError.message || 'Polling failed.');
          setIsProcessing(false);
          setPollingStatus('error');
          setTranscriptId(null);
        }
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pollingStatus, transcriptId]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      if (audioRef.current.ended) {
        audioRef.current.currentTime = 0;
      }
      audioRef.current.play().catch(err => {
        console.error("Audio play failed:", err);
      });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (event, newValue) => {
    if (audioRef.current && duration > 0) {
      const newTime = Math.max(0, Math.min(newValue, duration));
      setCurrentTime(newTime);
      audioRef.current.currentTime = newTime;
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(duration);
  };

  const highlightedWordIndex = useMemo(() => {
    if (!response?.words || currentTime === null) return -1;

    const currentTimeMs = currentTime * 1000;
    return response.words.findIndex(word =>
      currentTimeMs >= word.start && currentTimeMs < word.end
    );
  }, [currentTime, response?.words]);

  useEffect(() => {
    if (highlightedWordRef.current) {
      highlightedWordRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [highlightedWordIndex]);

  return (
    <Container>
      <LeftPane>
        <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3, paddingTop: '0px' }}>

          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              padding: 2,
              textAlign: 'center',
              backgroundColor: '#fafafa',
              transition: '0.3s',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Upload Audio File
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              sx={{
                mt: 2,
                backgroundColor: (theme) => theme.palette.secondary.main,
                color: '#fff',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.secondary.light,
                },
              }}
            >
              Choose File
              <input type="file" hidden onChange={handleFileUpload} accept="audio/*" disabled={isProcessing} />
            </Button>
            {file && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {file.name}
              </Typography>
            )}
            {pollingStatus === 'error' && errorMessage.includes('valid audio file') && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>{errorMessage}</Typography>
            )}
          </Box>

          {audioSrc && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: '#f9f9f9',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }}
            >
              <IconButton
                onClick={handlePlayPause}
                size="small"
                disabled={duration === 0 || pollingStatus === 'pending' || pollingStatus === 'uploading' || pollingStatus === 'error'}
              >
                {isPlaying ? <PauseCircleOutline fontSize="large" /> : <PlayCircleOutline fontSize="large" />}
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: '45px' }}>{formatTime(currentTime)}</Typography>
              <Slider
                size="small"
                value={currentTime}
                min={0}
                max={duration || 0}
                onChange={handleSeek}
                step={0.1}
                sx={{ mx: 1 }}
                disabled={duration === 0 || pollingStatus === 'pending' || pollingStatus === 'uploading' || pollingStatus === 'error'}
              />
              <Typography variant="body2" sx={{ minWidth: '45px' }}>{formatTime(duration)}</Typography>
              <audio
                ref={audioRef}
                src={audioSrc}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleAudioEnded}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onError={(e) => { console.error("Audio Error:", e); setResponse({ error: "Failed to load audio."})}}
                hidden
              />
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<RecordVoiceOver />}
            onClick={handleProcess}
            disabled={!file || isProcessing || pollingStatus === 'error'}
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.secondary.light,
              },
            }}
          >
            {isProcessing ? 'Processing...' : 'Start Transcription'}
          </Button>

          {isProcessing && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ textAlign: 'center', mb: 1 }}>
                {pollingStatus === 'uploading' ? 'Uploading file...' : 'Transcribing (checking status...)'}
              </Typography>
              <LinearProgress />
            </Box>
          )}

          <TranscriptBox sx={{ mt: 2 }}>
            {pollingStatus === 'error' && !errorMessage.includes('valid audio file') && (
              <Box sx={{ textAlign: 'center', pt: 4, color: 'error.main' }}>
                <ErrorOutline sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body1" fontWeight="bold">Transcription Failed</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{errorMessage}</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>Please try uploading the file again.</Typography>
              </Box>
            )}

            {pollingStatus === 'completed' && response?.words && (
              <Typography component="div" sx={{ lineHeight: 1.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main', mb: 1 }}>
                  <CheckCircleOutline sx={{ mr: 0.5 }}/>
                  <Typography variant="body2" fontWeight="bold">Transcription Complete</Typography>
                </Box>
                {response.words.map((word, index) => (
                  <span
                    key={`${index}-${word.start}`}
                    ref={index === highlightedWordIndex ? highlightedWordRef : null}
                    className={index === highlightedWordIndex ? 'highlight' : ''}
                  >
                    {word.text}{' '}
                  </span>
                ))}
              </Typography>
            )}

            {pollingStatus === 'completed' && response?.text && !response.words && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main', mb: 1 }}>
                  <CheckCircleOutline sx={{ mr: 0.5 }}/>
                  <Typography variant="body2" fontWeight="bold">Transcription Complete (No Timestamps)</Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {response.text}
                </Typography>
              </Box>
            )}

            {pollingStatus === 'idle' && !isProcessing && !errorMessage && (
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', pt: 4 }}>
                {file ? 'Click "Start Transcription" to process the audio.' : 'Upload an audio file and click "Start Transcription."'}
              </Typography>
            )}
          </TranscriptBox>
        </Box>
      </LeftPane>

      <RightPane sx={{ height: '80%' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <QuestionAnswer color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Transcript Assistant
            </Typography>
          </Box>

          <SummaryBox
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: 2,
              p: 2,
              backgroundColor: '#ffff',
            }}
          >
            {chatQueries.map((q, index) => (
              <Box key={index} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#1976d2' }}>Q: {q}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#333', ml: 1 }}>
                  A: {chatResponses[index]}
                </Typography>
              </Box>
            ))}
          </SummaryBox>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Type your question..."
              variant="outlined"
              size="small"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={pollingStatus !== 'completed' || !response?.words}
            />
            <IconButton
              color="primary"
              size="large"
              disabled={pollingStatus !== 'completed' || !response?.words || !question.trim()}
              onClick={() => {
                handleSubmitQuery(question);
                setQuestion('');
              }}
            >
              <Send />
            </IconButton>
          </Box>
        </Box>
      </RightPane>
    </Container>
  );
}
